import type { SVGProps } from 'react';

/**
 * The admin header's logo (brand file dawn-partol-admin-logo.svg): the wave
 * mark and a "DAWN PATROL ADMIN" wordmark.
 *
 * Inline rather than an <img>, so it costs no request; `role="img"` +
 * `aria-label` makes it one named image. The wave mark keeps its brand
 * colours (the `--color-logo-*` tokens); the wordmark follows the theme.
 * `viewBox="0 0 112 112"` crops it to the wave mark (the phone header).
 */
export function DawnPatrolAdminLogo(props: SVGProps<SVGSVGElement>) {
    return (
        <svg
            viewBox="0 0 611 112"
            fill="none"
            role="img"
            aria-label="Dawn Patrol Admin"
            {...props}
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M56.3645 2.44411C39.4405 2.44411 24.4598 10.2681 15.2415 22.2867H97.4876C88.2692 10.2681 73.2886 2.44411 56.3645 2.44411Z"
                fill="var(--color-logo-sand)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.615 78.8765C6.38005 78.3691 105.109 78.8765 105.109 78.8765C105.109 78.8765 90.8961 109.853 57.1088 109.853C23.3215 109.853 10.5317 87.3362 6.615 78.8765Z"
                fill="var(--color-logo-wave-1)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.98371 76.925H71.3296C71.3296 76.925 69.907 60.8082 87.5667 54.4943L2.03846 55.4972C2.03846 55.4972 1.36791 68.3889 6.98371 76.925Z"
                fill="var(--color-logo-wave-2)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M109.134 56.3637H88.9932C86.0521 56.3637 71.1745 62.2775 71.1745 77.2128H105.086C105.086 77.2128 109.134 64.5167 109.134 56.3637Z"
                fill="var(--color-logo-wave-3)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.29357 54.783H45.636C45.636 54.783 47.1959 39.8422 58.8083 37.0972H5.35434L1.29357 54.783Z"
                fill="var(--color-logo-wave-4)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M109.71 53.9204H47.45C47.45 53.9204 49.5422 37.5287 63.8064 37.5287H106.41L109.71 53.9204Z"
                fill="var(--color-logo-wave-5)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.89523 36.3774H21.6128L27.3195 23.0053H13.389L5.89523 36.3774Z"
                fill="var(--color-logo-wave-6)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M98.3759 23.0053H32.0977C32.0977 23.0053 23.7652 25.6102 21.8555 36.3774H107.84L98.3759 23.0053Z"
                fill="var(--color-logo-wave-7)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M56.0051 107.564C36.3433 107.564 19.2038 96.592 10.417 80.4647H101.593C92.8064 96.592 75.6668 107.564 56.0051 107.564ZM56.0053 4.15837C71.108 4.15837 84.7212 10.634 94.2051 20.9452H17.805C27.2889 10.634 40.9026 4.15837 56.0053 4.15837ZM94.1125 52.8493V52.8461C94.0724 52.8461 94.0329 52.8487 93.9923 52.8493H48.8054C49.8298 44.8004 56.7238 38.556 65.0548 38.556V38.5529H104.857C106.469 43.0651 107.469 47.8631 107.758 52.8493H94.1125ZM93.9922 57.008H107.834C107.683 63.8485 106.194 70.3647 103.618 76.3082H72.8102C73.8565 65.5293 82.946 57.0693 93.9922 57.008ZM52.6776 38.5529C48.2382 41.9103 45.1994 47.0232 44.6115 52.8493H4.25207C4.54081 47.8631 5.54128 43.0651 7.15305 38.5529H52.6776ZM20.5335 34.3944H8.84627C10.3618 31.0968 12.2139 27.9842 14.3605 25.1013H25.3688C22.7904 27.4926 21.0261 30.7439 20.5335 34.3944ZM103.164 34.3944H24.7465C25.7288 29.1172 30.369 25.1081 35.9322 25.1081V25.1013H97.6499C99.7965 27.9842 101.649 31.0968 103.164 34.3944ZM4.17563 57.008H80.1501C73.716 61.2146 69.2917 68.2354 68.6367 76.3082H8.39132C5.8155 70.3647 4.32598 63.8485 4.17563 57.008ZM56.0053 0C25.124 0 0 25.0592 0 55.8612C0 86.6627 25.124 111.722 56.0053 111.722C86.8865 111.722 112.01 86.6627 112.01 55.8612C112.01 25.0592 86.8865 0 56.0053 0Z"
                fill="var(--color-logo-crest)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M551.399 7.90894C558.264 7.90894 563.617 10.657 567.311 16.1443C570.962 21.5666 572.719 29.2255 572.719 38.9812C572.719 48.7367 570.949 56.4016 567.272 61.8386C563.551 67.3347 558.189 70.0906 551.321 70.0906C544.43 70.0905 539.056 67.3443 535.334 61.8601C531.655 56.4345 529.886 48.7337 529.886 38.9001C529.886 29.144 531.655 21.4977 535.335 16.0984L535.69 15.5945C539.4 10.4714 544.68 7.90899 551.399 7.90894ZM551.398 19.655C550.009 19.6551 548.834 20.0275 547.829 20.7507C546.875 21.4365 546.022 22.477 545.296 23.9333L545.153 24.2302C543.629 27.4862 542.819 32.3672 542.819 38.9792C542.819 45.5641 543.629 50.439 545.154 53.7058C545.911 55.3288 546.81 56.4695 547.815 57.2039C548.808 57.9291 549.962 58.3005 551.32 58.3005C552.699 58.3005 553.861 57.9352 554.849 57.2283C555.848 56.5137 556.733 55.4049 557.468 53.8269C558.952 50.6429 559.745 45.7323 559.745 38.9792C559.745 32.2244 558.958 27.3112 557.486 24.1257C556.756 22.547 555.879 21.439 554.889 20.7253C553.911 20.0197 552.762 19.655 551.398 19.655Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M238.109 42.6423L238.11 42.6472C238.342 44.2074 238.609 46.1831 238.915 48.572C239.201 46.2197 239.478 44.3108 239.75 42.8494L245.945 8.8269H258.673L264.873 42.863L264.874 42.8728C265.189 44.8118 265.477 46.7814 265.74 48.781C266.089 46.0122 266.371 43.9611 266.589 42.6316L271.637 8.8269H284.279L273.726 69.2947H258.747L253.053 37.8591C252.728 36.2619 252.439 34.5573 252.185 32.7478L251.496 37.9666L251.493 37.99L251.488 38.0134L245.951 69.2947H230.971L220.337 8.8269H232.9L238.109 42.6423Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M322.365 45.7107C322.301 44.4493 322.269 43.1227 322.269 41.7312V8.8269H334.12V69.2947H319.458L304.123 32.2625C304.213 33.5934 304.26 34.9558 304.26 36.3494V69.2947H292.332V8.8269H307.069L322.365 45.7107Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M378.252 8.8269C383.938 8.82702 388.396 10.3783 391.414 13.6638C394.415 16.932 395.82 21.7299 395.82 27.8562C395.82 34.2189 394.262 39.2699 390.991 42.8464C387.699 46.4387 382.994 48.1627 377.053 48.1628H375.514V69.2947H362.813V8.8269H378.252ZM375.513 36.573H376.434C378.808 36.5729 380.379 35.9057 381.372 34.7566L381.556 34.5281C382.453 33.331 383 31.3252 383 28.2957C383 25.3429 382.433 23.3659 381.495 22.1697C380.598 21.033 379.169 20.3759 376.976 20.3757H375.513V36.573Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M478.662 20.5359H467.831V69.2947H455.167V20.5359H444.336V8.8269H478.662V20.5359Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M502.126 8.8269C508.11 8.8269 512.748 10.2862 515.806 13.4216C518.853 16.5478 520.274 21.2235 520.274 27.2195C520.274 34.4356 518.015 39.8114 513.312 43.0623L524.469 69.2947H510.822L501.886 46.6111H499.968V69.2947H487.267V8.8269H502.126ZM499.969 35.1023H501.585C503.854 35.1023 505.242 34.448 506.049 33.3679C506.929 32.1903 507.456 30.3053 507.456 27.5408C507.456 24.6528 506.889 22.8744 506.018 21.9148C505.151 20.9622 503.726 20.3796 501.507 20.3796H499.969V35.1023Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M596.232 57.6248H610.814V69.2947H583.531V8.8269H596.232V57.6248Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M146.216 8.8269C153.114 8.8269 158.571 11.4777 162.453 16.7888C166.299 22.0526 168.155 29.335 168.155 38.5017C168.155 48.2436 166.232 55.8685 162.232 61.2214L162.231 61.2205C158.195 66.6244 152.376 69.2936 144.941 69.2937H130.001V8.8269H146.216ZM142.705 57.6228H145.017C146.778 57.6228 148.251 57.2445 149.476 56.53C150.7 55.816 151.736 54.7302 152.572 53.2214C154.268 50.1532 155.181 45.399 155.181 38.8191C155.181 32.3695 154.313 27.7021 152.699 24.6833C151.9 23.1902 150.934 22.1258 149.82 21.4314C148.709 20.7392 147.392 20.3757 145.828 20.3757H142.705V57.6228Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M218.77 69.2937H205.951L202.702 54.5837H189.716L186.467 69.2937H173.689L188.212 8.58472H204.207L218.77 69.2937ZM196.024 25.2449C195.528 27.8902 194.278 33.7446 192.286 42.7917H200.244L197.521 30.5642L197.52 30.5603L196.243 24.0427L196.024 25.2449Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M443.722 69.2937H430.903L427.654 54.5837H414.668L411.419 69.2937H398.641L413.165 8.58472H429.159L443.722 69.2937ZM420.976 25.2449C420.976 25.2449 419.231 33.7445 417.238 42.7917H425.196L422.473 30.5642L422.472 30.5603C422.288 29.7188 421.195 24.0427 421.195 24.0427L420.976 25.2449Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M261.874 102.999H256.84L245.559 85.8628V102.999H240.525V77.7946H245.559L256.84 95.0734V77.7946H261.874V102.999Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M232.008 77.7946V102.999H226.975V77.7946H232.008Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M218.454 78.0802V102.999H213.456V85.6129L207.066 102.999H202.71L196.32 85.6843V102.999H191.286V78.0802H197.391L204.959 96.8227L212.385 78.0802H218.454Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M183.555 90.361C183.555 92.86 183.031 95.0615 181.984 96.9655C180.937 98.8695 179.414 100.357 177.415 101.428C175.439 102.475 173.083 102.999 170.346 102.999H161.385V77.7946H170.346C173.083 77.7946 175.439 78.3182 177.415 79.3654C179.414 80.3888 180.937 81.8525 181.984 83.7565C183.031 85.6367 183.555 87.8382 183.555 90.361ZM169.918 98.5006C172.631 98.5006 174.725 97.7866 176.201 96.3586C177.7 94.9306 178.45 92.9314 178.45 90.361C178.45 87.7668 177.7 85.7557 176.201 84.3277C174.725 82.8997 172.631 82.1857 169.918 82.1857H166.419V98.5006H169.918Z"
                fill="var(--color-logo-wordmark)"
            />
            <path
                d="M147.637 97.9294H137.534L135.749 102.999H130.43L139.712 78.0802H145.459L154.706 102.999H149.386L147.637 97.9294ZM146.28 94.1095L142.568 83.4709L138.855 94.1095H146.28Z"
                fill="var(--color-logo-wordmark)"
            />
        </svg>
    );
}
